import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, CheckCheck, Download, ShieldCheck } from "lucide-react";
import { LeadRow } from "./LeadRow";
import { LeadEditDialog } from "./LeadEditDialog";
import { STATUS_META, errMsg } from "./utils";
import { usd, overBudget } from "./CostCard";

const FILTERS = [["all", "Όλα"], ["pending", STATUS_META.pending.label], ["approved", STATUS_META.approved.label], ["rejected", STATUS_META.rejected.label], ["verified", "✓ Site"]];
const match = (l, k) => k === "all" || (k === "verified" ? l.verify?.verdict === "yes" : l.status === k);

const download = async (project, onlyApproved, onlyVerified = false) => {
  try {
    const { data } = await api.get(`/agency/projects/${project.id}/export`, { params: { only_approved: onlyApproved, only_verified: onlyVerified }, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${project.client_name.replace(/\s+/g, "-").toLowerCase()}-leads.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(e?.response?.status === 400 ? "Δεν υπάρχουν leads για εξαγωγή" : errMsg(e));
  }
};

const blocked = (c, need) => {
  if (!overBudget(c, need)) return false;
  toast.error(`Εκτός budget: απομένουν ${usd(c.budget_left_usd, 3)} — αυξήστε το budget στο Brief`);
  return true;
};

const budgetNote = (c) => (c?.budget_usd ? `\nΥπόλοιπο budget: ${usd(c.budget_left_usd)} — σταματά αυτόματα στο όριο.` : "");

export const LeadsReview = ({ project, busy, onLeadsChange, onJobStarted }) => {
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const leads = project.leads || [];
  const shown = leads.filter((l) => match(l, filter));
  const replace = (upd) => onLeadsChange(leads.map((l) => (l.lead_id === upd.lead_id ? upd : l)));

  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/agency/projects/${project.id}/leads/${lead.lead_id}`, { status });
      replace({ ...lead, status });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const approveAllPending = async () => {
    const pend = leads.filter((l) => l.status === "pending");
    const res = await Promise.allSettled(pend.map((l) => api.patch(`/agency/projects/${project.id}/leads/${l.lead_id}`, { status: "approved" })));
    const ok = new Set(pend.filter((_, i) => res[i].status === "fulfilled").map((l) => l.lead_id));
    onLeadsChange(leads.map((l) => (ok.has(l.lead_id) ? { ...l, status: "approved" } : l)));
    if (ok.size < pend.length) toast.error(`Απέτυχαν ${pend.length - ok.size} εγκρίσεις`);
    else toast.success(`Εγκρίθηκαν ${ok.size} leads`);
  };

  const enrich = async () => {
    const todo = leads.filter((l) => l.status !== "rejected" && !l.enriched);
    if (!todo.length) return toast.info("Όλα τα leads έχουν ήδη εμπλουτιστεί");
    if (blocked(project.cost, 0.01)) return;
    if (!window.confirm(`Θα εμπλουτιστούν ${todo.length} leads (τηλέφωνα δωρεάν + AI).\nΚόστος: ~${usd(todo.length * 0.01)}${budgetNote(project.cost)}\n\nΣυνέχεια;`)) return;
    try {
      await api.post(`/agency/projects/${project.id}/enrich`, { lead_ids: todo.map((l) => l.lead_id), phones: true, ai: true });
      onJobStarted("enriching");
      toast.info("Ξεκίνησε εμπλουτισμός: τηλέφωνα + «Γιατί ταιριάζει»");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const verify = async () => {
    let todo = leads.filter((l) => l.status !== "rejected" && !l.verify);
    let force = false;
    if (!todo.length) {
      if (!window.confirm("Όλα τα leads έχουν ήδη ελεγχθεί. Να γίνει νέος έλεγχος (π.χ. μετά από αλλαγή κανόνων);")) return;
      todo = leads.filter((l) => l.status !== "rejected");
      force = true;
    }
    if (blocked(project.cost, 0.015)) return;
    const sites = new Set(todo.map((l) => (l.company_domain || l.company_website || l.lead_id).toLowerCase())).size;
    if (!window.confirm(`Έλεγχος site: ${sites} sites (scraping δωρεάν + AI).\nΚόστος: ~${usd(sites * 0.015)}${budgetNote(project.cost)}\n\nΣυνέχεια;`)) return;
    const autoReject = window.confirm(`Έλεγχος site για ${todo.length} leads.\n\nOK = απόρριψη αυτόματα όσων ΔΕΝ ταιριάζουν\nCancel = μόνο σήμανση, χωρίς απόρριψη`);
    try {
      await api.post(`/agency/projects/${project.id}/verify`, { lead_ids: todo.map((l) => l.lead_id), auto_reject: autoReject, force });
      onJobStarted("verifying");
      toast.info("Ξεκίνησε ο έλεγχος των sites");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="agency-leads-review">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1 bg-slate-100 p-1 rounded-md">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`agency-filter-${k}`}
              className={`px-3 py-1 text-xs rounded transition-colors duration-150 ${filter === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
              {label} ({leads.filter((l) => match(l, k)).length})
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={verify} disabled={busy || !leads.length} data-testid="agency-verify-btn">
            <ShieldCheck className="w-4 h-4 mr-1" /> Έλεγχος site
          </Button>
          <Button variant="outline" size="sm" onClick={enrich} disabled={busy || !leads.length} data-testid="agency-enrich-btn">
            <Sparkles className="w-4 h-4 mr-1" /> Εμπλουτισμός (τηλ. + AI)
          </Button>
          <Button variant="outline" size="sm" onClick={approveAllPending} disabled={busy || !leads.some((l) => l.status === "pending")} data-testid="agency-approve-all-btn">
            <CheckCheck className="w-4 h-4 mr-1" /> Έγκριση όλων
          </Button>
          <Button size="sm" onClick={() => download(project, true)} disabled={busy} data-testid="agency-export-approved-btn">
            <Download className="w-4 h-4 mr-1" /> Excel (εγκεκριμένα)
          </Button>
          <Button variant="ghost" size="sm" onClick={() => download(project, false)} disabled={busy} data-testid="agency-export-all-btn">
            Excel (όλα εκτός απορριφθέντων)
          </Button>
          <Button variant="ghost" size="sm" onClick={() => download(project, false, true)} disabled={busy} data-testid="agency-export-verified-btn">
            Excel (μόνο ✓ site)
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
        <table className="w-full text-sm min-w-[1200px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
            <tr>{["Εταιρεία", "Επαφή", "Emails (domain)", "Τηλέφωνο", "Απόδειξη site", "Γιατί ταιριάζει", "Κατάσταση", ""].map((h) => <th key={h} className="text-left p-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map((l) => <LeadRow key={l.lead_id} lead={l} busy={busy} onStatus={setStatus} onEdit={setEditing} />)}
            {!shown.length && <tr><td colSpan={8} className="p-8 text-center text-slate-500" data-testid="agency-leads-empty">Δεν υπάρχουν leads. Τρέξτε μια αναζήτηση.</td></tr>}
          </tbody>
        </table>
      </div>
      <LeadEditDialog projectId={project.id} lead={editing} onClose={() => setEditing(null)} onSaved={(l) => { replace(l); setEditing(null); }} />
    </div>
  );
};
